import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Users, TrendingUp, Database, Video as VideoIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const AdminDashboard = () => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [analytics, setAnalytics] = useState(null);
 const [users, setUsers] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchAdminData();
 }, []);

 const fetchAdminData = async () => {
 try {
 const [analyticsRes, usersRes] = await Promise.all([
 axios.get(`${API}/admin/analytics`),
 axios.get(`${API}/admin/users`)
 ]);
 setAnalytics(analyticsRes.data);
 // /admin/users returns {users: [...], total} — unwrap safely so the
 // `.map` in the users table doesn't blow up.
 const list = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []);
 setUsers(list);
 } catch (error) {
 toast.error('Failed to load admin data');
 } finally {
 setLoading(false);
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-background flex items-center justify-center">
 <p className="text-slate-500">Loading admin dashboard...</p>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-background">
 <div className="bg-white shadow-sm border-b">
 <div className="container mx-auto px-4 py-4 flex items-center gap-4">
 <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
 <ArrowLeft className="w-5 h-5" />
 </Button>
 <h1 className="text-2xl font-bold">Admin Dashboard</h1>
 </div>
 </div>

 <div className="container mx-auto px-4 py-8">
 {/* Stats Overview */}
 <div className="grid md:grid-cols-3 gap-6 mb-8">
 <Card className="shadow-lg" data-testid="total-users-card">
 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
 <CardTitle className="text-sm font-medium">Total Users</CardTitle>
 <Users className="h-4 w-4 text-muted-foreground" />
 </CardHeader>
 <CardContent>
 <div className="text-3xl font-bold">{analytics?.total_users || 0}</div>
 </CardContent>
 </Card>

 <Card className="shadow-lg" data-testid="free-users-card">
 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
 <CardTitle className="text-sm font-medium">Free Users</CardTitle>
 <Users className="h-4 w-4 text-blue-500" />
 </CardHeader>
 <CardContent>
 <div className="text-3xl font-bold text-blue-600">{analytics?.free_users || 0}</div>
 </CardContent>
 </Card>

 <Card className="shadow-lg" data-testid="pro-users-card">
 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
 <CardTitle className="text-sm font-medium">Pro Users</CardTitle>
 <TrendingUp className="h-4 w-4 text-green-500" />
 </CardHeader>
 <CardContent>
 <div className="text-3xl font-bold text-green-600">{analytics?.pro_users || 0}</div>
 </CardContent>
 </Card>
 </div>

 {/* Detailed Analytics */}
 <Tabs defaultValue="users" className="w-full">
 <TabsList className="grid w-full md:w-auto grid-cols-4 mb-6">
 <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
 <TabsTrigger value="brands" data-testid="brands-tab">Brands</TabsTrigger>
 <TabsTrigger value="geography" data-testid="geography-tab">Geography</TabsTrigger>
 <TabsTrigger value="products" data-testid="products-tab">Products</TabsTrigger>
 </TabsList>

 <TabsContent value="users">
 <Card className="shadow-lg">
 <CardHeader>
 <CardTitle>All Users</CardTitle>
 <CardDescription>Complete list of registered users</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b">
 <th className="text-left py-3 px-4">Email</th>
 <th className="text-left py-3 px-4">Role</th>
 <th className="text-left py-3 px-4">Created</th>
 </tr>
 </thead>
 <tbody>
 {users.map(user => (
 <tr key={user.id} className="border-b hover:bg-slate-50" data-testid="user-row">
 <td className="py-3 px-4">{user.email}</td>
 <td className="py-3 px-4">
 <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
 user.role === 'admin' ? 'bg-primary/5 text-primary' :
 user.role === 'pro' ? 'bg-green-100 text-green-800' :
 'bg-blue-100 text-blue-800'
 }`}>
 {user.role.toUpperCase()}
 </span>
 </td>
 <td className="py-3 px-4">{new Date(user.created_at).toLocaleDateString()}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="brands">
 <Card className="shadow-lg">
 <CardHeader>
 <CardTitle>User Distribution by Brand</CardTitle>
 <CardDescription>Freeze dryer brands owned by users</CardDescription>
 </CardHeader>
 <CardContent>
 {analytics?.by_brand && analytics.by_brand.length > 0 ? (
 <div className="space-y-3">
 {analytics.by_brand.map((item, index) => (
 <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded" data-testid="brand-stat">
 <span className="font-medium">{item._id || 'Not Specified'}</span>
 <span className="text-2xl font-bold text-blue-600">{item.count}</span>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-slate-500">No brand data available</p>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="geography">
 <Card className="shadow-lg">
 <CardHeader>
 <CardTitle>User Distribution by Country</CardTitle>
 <CardDescription>Geographic distribution of users</CardDescription>
 </CardHeader>
 <CardContent>
 {analytics?.by_country && analytics.by_country.length > 0 ? (
 <div className="space-y-3">
 {analytics.by_country.map((item, index) => (
 <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded" data-testid="country-stat">
 <span className="font-medium">{item._id || 'Not Specified'}</span>
 <span className="text-2xl font-bold text-green-600">{item.count}</span>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-slate-500">No geography data available</p>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="products">
 <Card className="shadow-lg">
 <CardHeader>
 <CardTitle>Product Focus Distribution</CardTitle>
 <CardDescription>What users are freeze-drying</CardDescription>
 </CardHeader>
 <CardContent>
 {analytics?.by_product && analytics.by_product.length > 0 ? (
 <div className="space-y-3">
 {analytics.by_product.map((item, index) => (
 <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded" data-testid="product-stat">
 <span className="font-medium capitalize">{(item._id || 'Not Specified').replace('_', ' ')}</span>
 <span className="text-2xl font-bold text-primary">{item.count}</span>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-slate-500">No product data available</p>
 )}
 </CardContent>
 </Card>
 </TabsContent>
 </Tabs>

 {/* Quick Actions */}
 <div className="mt-8 grid md:grid-cols-2 gap-6">
 <Card className="shadow-lg">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Database className="w-5 h-5" />
 Manage Database
 </CardTitle>
 <CardDescription>Add or edit freeze-drying product data</CardDescription>
 </CardHeader>
 <CardContent>
 <Button onClick={() => navigate('/database')} className="w-full" data-testid="manage-database-btn">
 Go to Database
 </Button>
 </CardContent>
 </Card>

 <Card className="shadow-lg">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <VideoIcon className="w-5 h-5" />
 Manage Videos
 </CardTitle>
 <CardDescription>Add or remove video content</CardDescription>
 </CardHeader>
 <CardContent>
 <Button onClick={() => navigate('/videos')} className="w-full" data-testid="manage-videos-btn">
 Go to Videos
 </Button>
 </CardContent>
 </Card>
 </div>
 </div>
 </div>
 );
};

export default AdminDashboard;
